'use client';

import Link from 'next/link';
import styles from './QuestionnairePageHeader.module.css';
import type { QuestionnairePageHeaderProps } from './QuestionnairePageHeader.types';
import {
  Alert,
  Button,
  ButtonGroup,
  Column,
  Text,
  Shimmer,
  Flex,
  Heading,
} from '@/_components';
import { FC, useContext } from 'react';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';

export const QuestionnairePageHeader: FC<QuestionnairePageHeaderProps> = ({
  isLoading,
  name,
  mode,
  selectedCompanyHash,
  taxYear,
}) => {
  const { preferences } = useContext(FirmSettingsContext);

  const isTemplatePreview: boolean = mode === 'admin-template';
  const isPreview: boolean = mode !== 'client';

  const year: number | undefined = isTemplatePreview
    ? (taxYear ?? 0) - 1
    : taxYear;

  return (
    <>
      <Flex
        isYCentered={true}
        isBetween={true}
        isWrapping={true}
        gap={6}
        className={styles.questionnairePageHeader}
      >
        <Flex
          isYCentered={true}
          gap={4}
          className={styles.questionnairePageButton}
        >
          <ButtonGroup className={styles.questionnairePageButton__buttonGroup}>
            <Link href="/">
              <Button
                value="Back"
                intent="ghost"
                size="small"
                isDisabled={isPreview}
                iconLeft="caret-left"
                isIconOnly={true}
              />
            </Link>
          </ButtonGroup>
          {isLoading ? (
            <Shimmer height={36} width={180} />
          ) : (
            <Heading level={1}>
              <em>{year ? `${year} ` : ''}</em>
              {name}
            </Heading>
          )}
        </Flex>
        <Column width={380}>
          <Text
            type="small"
            className={styles.questionnaireHelper}
            color="secondary"
          >
            Your progress is saved automatically.{' '}
            {!preferences.communication_disabled && (
              <>
                If you&apos;re not sure how to answer a question, please{' '}
                <Link
                  href={
                    selectedCompanyHash ? `/inbox/${selectedCompanyHash}` : '#'
                  }
                >
                  send us a message
                </Link>
                .
              </>
            )}
          </Text>
        </Column>
      </Flex>
      {isTemplatePreview && (
        <Alert type="info">
          This is a <b>preview</b> of the template. Template tags like{' '}
          <code>&#123;client_fname&#125;</code> will be replaced with actual
          data when the template is sent to your clients.
        </Alert>
      )}
    </>
  );
};
